import { zodResolver } from '@hookform/resolvers/zod';
import {
  Accordion,
  Badge,
  Button,
  Card,
  Col,
  Flex,
  FormContainer,
  Icon,
  MarkupPreview,
  Row,
  Title,
} from '@wyz/ui';
import { useForm, useWatch } from 'react-hook-form';
import {
  CheckboxField,
  CriteriaBuilder,
  DateField,
  MatchedCustomersTable,
  RichTextEditorField,
  SelectField,
  TextField,
} from '~/common/components';
import { Trans, useI18n } from '~/common/context';
import { advertForm } from '~/connect/schemas';
import { IAdvertForm, IAdvertPayload } from '~/connect/types';

import { CUSTOMER_LEVEL, ValidationErrorResponse } from '@wyz/types';
import { DateUtils, invariant } from '@wyz/utils';
import { Link } from 'react-router';
import { useManageCriteria } from '~/common/api';
import { useBackTo, useMyCustomers, useValidationForm } from '~/common/hooks';
import { FEATURE_CONTEXT } from '~/common/schemas';
import { CommonErrorCodesEnumType } from '~/common/types';
import { SelectLanguage } from '~/connect/components';
import { AdvertHelpers } from '~/connect/helpers';

const DEFAULT_VALUES: Partial<IAdvertForm> = {
  criteria: [],
  customerScope: [],
};

type Props =
  | {
      mode?: 'CREATE' | 'UPDATE';
      defaultValues?: Partial<IAdvertForm>;
      isLoading: boolean;
      onSubmit: (data: IAdvertPayload) => void;
      errors?: ValidationErrorResponse<CommonErrorCodesEnumType> | null;
    }
  | {
      mode?: 'READ_ONLY';
      defaultValues?: Partial<IAdvertForm>;
      isLoading?: never;
      onSubmit?: never;
      errors?: never;
    };

export function AdvertForm({
  mode = 'CREATE',
  defaultValues = DEFAULT_VALUES,
  isLoading,
  onSubmit,
  errors,
}: Props) {
  const backTo = useBackTo('/platform/distribution/adverts');
  const { t } = useI18n();

  const methods = useForm<IAdvertForm>({
    defaultValues,
    resolver: zodResolver(advertForm),
  });

  useValidationForm({
    methods,
    errors: errors?.errors,
    t,
  });

  const manageCriteria = useManageCriteria({ getAll: { enabled: true } });
  const watchedSummary = useWatch({
    control: methods.control,
    name: 'summary',
  });
  const getCustomersByUser = useMyCustomers({
    minLevel: CUSTOMER_LEVEL.ROOT,
  });
  const watchedCustomerIds = useWatch({
    control: methods.control,
    name: 'customerScope',
  });

  return (
    <FormContainer
      methods={methods}
      submitHandler={(data) => {
        invariant(manageCriteria.getAll.isSuccess);
        onSubmit?.(
          AdvertHelpers.fromFormToPayload(
            data,
            manageCriteria.getAll.data.data,
          ),
        );
      }}
      formConfiguration={{
        mode: mode === 'READ_ONLY' ? 'READ_ONLY' : 'WRITE',
      }}
    >
      <Card className='mb-section'>
        <Card.Header>
          <Title heading='lg' color='tertiary' weight='bold'>
            <Icon icon='post' width='1.5rem' height='1.5rem' />
            <span className='ms-2'>
              <Trans i18nKey='app.common.general_information' />
            </span>
          </Title>
        </Card.Header>
        <Card.Body>
          <Row>
            <Col md={6}>
              <TextField
                name='title'
                label={<Trans i18nKey='app.common.title' />}
                required
              />
            </Col>
            <Col md={6}>
              <SelectLanguage
                name={'languageIsoCode'}
                label={<Trans i18nKey='app.common.language' />}
                required
                mode={mode}
              />
            </Col>
          </Row>
          <Row>
            <Col md={6}>
              <DateField
                name='startDate'
                label={<Trans i18nKey='app.common.start_date' />}
                placeholderText='dd-MM-yyyy'
                dateFormat={DateUtils.DATE_FORMAT.DMY}
                translations={{
                  applyButton: t('app.common.button.apply'),
                  resetButton: t('app.common.button.reset'),
                }}
              />
            </Col>
            <Col md={6}>
              <DateField
                name='endDate'
                label={<Trans i18nKey='app.common.end_date' />}
                dateFormat={DateUtils.DATE_FORMAT.DMY}
                placeholderText='dd-MM-yyyy'
                translations={{
                  applyButton: t('app.common.button.apply'),
                  resetButton: t('app.common.button.reset'),
                }}
              />
            </Col>
          </Row>
          <Row>
            <Col md={12}>
              <SelectField<true>
                name='customerScope'
                label={<Trans i18nKey='app.advert.form.customer_scope.label' />}
                options={
                  getCustomersByUser.data?.data
                    .filter((item) => item.level <= CUSTOMER_LEVEL.N1)
                    .map((item) => ({
                      label: item.name,
                      value: item.id,
                    })) || []
                }
                isMulti={true}
                maxShown={Infinity}
                wrapItems
                required
                isLoading={getCustomersByUser.isLoading}
                renderReadMode={(options) =>
                  options?.length ? (
                    <Flex className='flex-wrap'>
                      {options.map((option) => (
                        <Badge
                          key={option.value}
                          pill
                          bg='light-gray'
                          className='me-1 mb-1'
                        >
                          {option.label}
                        </Badge>
                      ))}
                    </Flex>
                  ) : (
                    '-'
                  )
                }
              />
            </Col>
          </Row>
        </Card.Body>
      </Card>
      <Card className='mb-section'>
        <Card.Header>
          <Title heading='lg' color='tertiary' weight='bold'>
            <Icon icon='clip-board' width='1.5rem' height='1.5rem' />
            <span className='ms-2'>
              <Trans i18nKey='app.common.criteria' />
            </span>
          </Title>
        </Card.Header>
        <Card.Body>
          <CriteriaBuilder
            name={'criteria'}
            visibilityFilter={{ customerIds: watchedCustomerIds }}
          >
            <Row className='mt-4'>
              <Col md={6}>
                <MatchedCustomersTable
                  featureContext={FEATURE_CONTEXT.ADVERT}
                  name={'criteria'}
                  matchUserContext
                  queryOverrideParams={{
                    parentIds: watchedCustomerIds,
                  }}
                />
              </Col>
            </Row>
          </CriteriaBuilder>
          <CheckboxField
            label={
              <Trans i18nKey='app.advert.form.apply_to_all_customers_children' />
            }
            name='applyToAllCustomersChildren'
          />
        </Card.Body>
      </Card>
      <Card className='mb-section'>
        <Card.Header>
          <Title heading='lg' color='tertiary' weight='bold'>
            <Icon icon='clip-board' width='1.5rem' height='1.5rem' />
            <span className='ms-2'>
              <Trans i18nKey='app.advert.form.summary.title' />
            </span>
          </Title>
        </Card.Header>
        <Card.Body>
          {mode === 'READ_ONLY' ? (
            <Flex className='fit-sub-images' alignItems='center'>
              <MarkupPreview html={watchedSummary} />
            </Flex>
          ) : (
            <>
              <RichTextEditorField name='summary' />
              {watchedSummary && (
                <Accordion.Container flush defaultActiveKey='0'>
                  <Accordion.Item eventKey='0'>
                    <Accordion.Header>
                      <Title heading='lg' color='tertiary' weight='bold'>
                        <Trans i18nKey='app.advert.summary.preview' />
                      </Title>
                    </Accordion.Header>
                    <Accordion.Body>
                      <Flex className='fit-sub-images' alignItems='center'>
                        <MarkupPreview html={watchedSummary} />
                      </Flex>
                    </Accordion.Body>
                  </Accordion.Item>
                </Accordion.Container>
              )}
            </>
          )}
        </Card.Body>
      </Card>
      <Card className='mb-section'>
        <Card.Body>
          <Flex className='space-x-4' justifyContent='between'>
            <Button
              id='cancel'
              as={Link}
              variant='secondary'
              type='button'
              to={backTo}
            >
              <Trans i18nKey='app.common.button.cancel' />
            </Button>
            {mode !== 'READ_ONLY' && (
              <Button
                id='submit'
                variant='primary'
                type='submit'
                disabled={isLoading}
              >
                {mode === 'CREATE'
                  ? t('app.advert.form.buttons.create')
                  : t('app.advert.form.buttons.update')}
              </Button>
            )}
          </Flex>
        </Card.Body>
      </Card>
    </FormContainer>
  );
}
